// src/EditProfileForm.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { fetchJson, fetchForm, API_BASE } from './api';
import {
  Box, Paper, Grid, TextField, Button, Typography, CircularProgress,
  Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  Slider, Autocomplete, Chip, Switch, FormControlLabel, Stack, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';
import useDirtyGuard from './hooks/useDirtyGuard';
import { normalizeRoles, normalizeSkills, canonicalizeRole, canonicalizeSkill } from './utils/normalize';


const ROLE_PRESETS = [
  'Finalizzatore', 'Editor Video', 'Colorist', 'Assistente al montaggio', 'Post Producer'
];

const SKILL_PRESETS = [
  'Avid Media Composer', 'DaVinci Resolve', 'Adobe Premiere Pro',
  'After Effects', 'Color grading', 'Conforming',
  'Audio cleanup', 'Motion graphics', 'Storytelling', 'Broadcast deliverables'
];

// evita duplicati (case-insensitive)
const addUnique = (arr, value) => {
  const norm = (s) => String(s).trim().toLowerCase();
  return arr.some((x) => norm(x) === norm(value)) ? arr : [...arr, value];
};



// ---- helper URL immagine (anche relative /uploads) ----
const resolveProfileImageUrl = (raw, version = Date.now()) => {
  if (!raw) return undefined;
  if (raw.startsWith('data:')) return raw;               // preview locale dopo crop
  if (/^https?:\/\//i.test(raw)) return `${raw}?v=${version}`;
  if (raw.startsWith('/')) return `${API_BASE}${raw}?v=${version}`;
  return `${API_BASE}/${raw.replace(/^\/+/, '')}?v=${version}`;
};

// ---------------- helpers immagine ----------------
function createImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getCroppedFile(imageSrc, cropPixels) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;

  ctx.drawImage(
    image,
    cropPixels.x, cropPixels.y,
    cropPixels.width, cropPixels.height,
    0, 0,
    cropPixels.width, cropPixels.height
  );

  return new Promise((resolve) =>
    canvas.toBlob((blob) => {
      resolve(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92)
  );
}

// ---------------- componente ----------------

const COUNTRIES = [
  { code: 'AE', label: '🇦🇪 Emirati Arabi' },
  { code: 'AR', label: '🇦🇷 Argentina' },
  { code: 'AT', label: '🇦🇹 Austria' },
  { code: 'AU', label: '🇦🇺 Australia' },
  { code: 'BE', label: '🇧🇪 Belgio' },
  { code: 'BR', label: '🇧🇷 Brasile' },
  { code: 'CA', label: '🇨🇦 Canada' },
  { code: 'CH', label: '🇨🇭 Svizzera' },
  { code: 'DE', label: '🇩🇪 Germania' },
  { code: 'DK', label: '🇩🇰 Danimarca' },
  { code: 'ES', label: '🇪🇸 Spagna' },
  { code: 'FI', label: '🇫🇮 Finlandia' },
  { code: 'FR', label: '🇫🇷 Francia' },
  { code: 'GB', label: '🇬🇧 Regno Unito' },
  { code: 'GR', label: '🇬🇷 Grecia' },
  { code: 'IT', label: '🇮🇹 Italia' },
  { code: 'JP', label: '🇯🇵 Giappone' },
  { code: 'MX', label: '🇲🇽 Messico' },
  { code: 'NL', label: '🇳🇱 Paesi Bassi' },
  { code: 'NO', label: '🇳🇴 Norvegia' },
  { code: 'PL', label: '🇵🇱 Polonia' },
  { code: 'PT', label: '🇵🇹 Portogallo' },
  { code: 'RO', label: '🇷🇴 Romania' },
  { code: 'SE', label: '🇸🇪 Svezia' },
  { code: 'US', label: '🇺🇸 Stati Uniti' },
  { code: 'OTHER', label: '🌍 Altro' },
];
export default function EditProfileForm({ onProfileUpdated, onCancel }) {
  // master
  const [rolesMaster, setRolesMaster] = useState({}); // { Macro: [ruoli] }
  const [skillsMaster, setSkillsMaster] = useState([]);

  // profilo
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');

  const [ruoli, setRuoli] = useState([]);   // string[]
  const [skills, setSkills] = useState([]); // string[]

  const [bio, setBio] = useState('');
  const [cities, setCities] = useState([]);
  const [availableTravel, setAvailableTravel] = useState(false);
  const [availableRemote, setAvailableRemote] = useState(false);
  const [country, setCountry] = useState('IT');
const BIO_MAX = 600;

  const [notifyAlerts, setNotifyAlerts] = useState(true);
  const [notifyMessages, setNotifyMessages] = useState(true);


  // crop dialog
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.2);

  // 🔒 Guard uscita senza salvataggio (hook dedicato)
  const { markDirty, resetDirty, confirmIfDirty } = useDirtyGuard();

  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((_c, area) => setCroppedAreaPixels(area), []);

  const onDrop = useCallback(async (accepted) => {
    if (!accepted?.length) return;
    const file = accepted[0];
    const compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1600 });
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCropOpen(true); // si apre SOLO dopo aver scelto un file
    };
    reader.readAsDataURL(compressed);
  }, []);

  const { getRootProps, getInputProps, open: openFileDialog } = useDropzone({
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [], 'image/gif': [] }, multiple: false, onDrop, noClick: true
  });

  // opzioni ruoli raggruppate
  const roleOptions = useMemo(() => {
    const out = [];
    for (const macro of Object.keys(rolesMaster || {})) {
      (rolesMaster[macro] || []).forEach(r => out.push({ label: r, group: macro }));
    }
    return out;
  }, [rolesMaster]);

  const roleToGroup = useMemo(() => {
    const m = {};
    for (const g of Object.keys(rolesMaster || {})) {
      (rolesMaster[g] || []).forEach(r => { m[r] = g; });
    }
    return m;
  }, [rolesMaster]);

  // load iniziale
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [rm, sm, me, meUser] = await Promise.all([
          fetchJson('/ruoli'),
          fetchJson('/skills'),
          fetchJson('/freelancers/self'),
          fetchJson('/auth/me'),
        ]);
        if (!alive) return;

        setRolesMaster(rm || {});
        setSkillsMaster(Array.isArray(sm) ? sm : []);

        setNome(me?.nome || '');
        setCognome(me?.cognome || '');
        setTelefono(me?.telefono || '');
        setEmail(me?.email || '');
        setProfileImageUrl(me?.profile_image_url || '');
        setRuoli(Array.isArray(me?.ruoli) ? me.ruoli : []);
        setSkills(Array.isArray(me?.skills) ? me.skills : []);
        setCities(Array.isArray(me?.cities) ? me.cities : []);
        setAvailableTravel(me?.available_travel || false);
        setAvailableRemote(me?.available_remote || false);
        setCountry(me?.country || 'IT');
        setBio(
  (typeof me?.bio === 'string' ? me.bio :
   (typeof me?.descrizione === 'string' ? me.descrizione : '')) || ''
);
        if (meUser?.notify_alerts !== undefined) setNotifyAlerts(meUser.notify_alerts);
        if (meUser?.notify_messages !== undefined) setNotifyMessages(meUser.notify_messages);

      } catch (e) {
        if (!alive) return;
        setError(e.message || 'Errore nel caricamento del profilo.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // salvataggio
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let finalUrl = profileImageUrl;

      // Se c'è un crop da caricare
      if (imageSrc && croppedAreaPixels) {
        const file = await getCroppedFile(imageSrc, croppedAreaPixels);

        const formData = new FormData();
        formData.append('image', file);

        // Upload con helper (aggiunge Authorization)
        const up = await fetchForm('/upload/profile-image', formData);
        finalUrl = up.url; // es.: "/uploads/xxx.jpg"
        setProfileImageUrl(finalUrl);
        setCropOpen(false);
        setImageSrc(null);
      }

      const payload = {
        nome, cognome, telefono,
        profile_image_url: finalUrl,
        ruoli: Array.isArray(ruoli) ? ruoli : [],
        skills: Array.isArray(skills) ? skills : [],
        bio,                 // se il backend legge "bio"
        descrizione: bio,    // se il backend legge "descrizione"
        cities: Array.isArray(cities) ? cities : [],
        available_travel: availableTravel,
        available_remote: availableRemote,
        country: country || 'IT',
      };

      await fetchJson('/freelancers/self', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      // Salva preferenze notifiche
      await fetchJson('/auth/notifications', {
        method: 'PATCH',
        body: JSON.stringify({ notify_alerts: notifyAlerts, notify_messages: notifyMessages }),
      }).catch(() => {}); // non blocca il salvataggio se fallisce

      if (telefono) {
        localStorage.setItem('cellulare', telefono);
        localStorage.setItem('telefono', telefono);
      }

      // ✅ uscita pulita: nessun prompt dopo il salvataggio
      resetDirty();

      if (typeof onProfileUpdated === 'function') onProfileUpdated(payload);
    } catch (eSave) {
      setError(eSave.message || 'Errore nel salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={22} />
        <Typography>Carico il tuo profilo…</Typography>
      </Box>
    );
  }

  const pillInputSx = {
    '& .MuiInputBase-root': { borderRadius: 999, height: 44, px: 2 },
    '& .MuiChip-root': { borderRadius: 999 }
  };

  return (
    <Paper sx={{ p: 3 }}>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {/* wrapper dropzone invisibile per gestire il file input */}
      <Box {...getRootProps()}>
        <input {...getInputProps()} />
      </Box>

      {/* 👇 intercetta tutte le modifiche dei campi figli */}
      <Box component="form" onSubmit={handleSave}>
        <Grid container spacing={2} alignItems="center">
          {/* Avatar + Upload */}
          <Grid item xs={12} md="auto">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={resolveProfileImageUrl(profileImageUrl)}
                sx={{ width: 80, height: 80, cursor: 'pointer' }}
                onClick={openFileDialog}
                title="Clicca per cambiare foto"
              />
              <Button variant="outlined" onClick={openFileDialog}>
                Carica foto
              </Button>
              <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.5, textAlign: 'center' }}>
                JPG, PNG, WebP (no HEIC/HEIF)
              </Typography>
            </Box>
          </Grid>

          {/* Dati anagrafici */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth label="Nome" value={nome} onChange={(e) => setNome(e.target.value)}
              sx={pillInputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth label="Cognome" value={cognome} onChange={(e) => setCognome(e.target.value)}
              sx={pillInputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth label="Cellulare" value={telefono} onChange={(e) => setTelefono(e.target.value)}
              sx={pillInputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth label="Email" value={email} disabled sx={pillInputSx}
            />
          </Grid>

          {/* Ruoli */}
          <Grid item xs={12} md={6}>
            <Autocomplete
  multiple
  options={roleOptions}
  value={ruoli.map(r => ({ label: r, group: roleToGroup[r] || '' }))}
  onChange={(_, newVal) => setRuoli(normalizeRoles((newVal || []).map(v => v.label || v)))}
  getOptionLabel={(o) => o.label}
  groupBy={(o) => o.group}
  renderTags={(value, getTagProps) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
    {value.map((opt, idx) => (
      <Chip
        {...getTagProps({ index: idx })}
        label={opt.label}
        size="small"
        color="primary"
        variant="outlined"
        sx={{
          fontWeight: 600,
          borderColor: 'primary.light',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    ))}
  </Box>
)}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Ruoli"
      placeholder="Aggiungi ruolo"
      fullWidth
      sx={{
        ...pillInputSx,
        '& .MuiInputBase-root': {
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          rowGap: 0.5,
        },
      }}
    />
  )}
  ListboxProps={{
    sx: {
      '& .MuiAutocomplete-groupLabel': {
        fontWeight: 700,
        position: 'sticky', top: 0,
        bgcolor: 'background.paper', zIndex: 1, px: 1, py: 0.5,
      }
    }
  }}
  componentsProps={{ paper: { sx: { width: 520 } } }}
  sx={{ width: '100%', ...pillInputSx }}
  filterSelectedOptions
/>

            {/* Suggerimenti ruoli */}
{Array.isArray(ruoli) && (
  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
    {ROLE_PRESETS
      .filter(p => !ruoli.some(r => String(r).toLowerCase() === String(p).toLowerCase()))
      .map((p) => (
        <Chip
          key={p}
          label={p}
          variant="outlined"
          onClick={() => setRuoli(prev => addUnique(prev ?? [], p))}
          sx={{ mb: 1, cursor: 'pointer' }}
        />
      ))}
  </Stack>
)}

          </Grid>

          {/* Paese */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={pillInputSx}>
              <InputLabel>Paese</InputLabel>
              <Select value={country} label="Paese" onChange={e => setCountry(e.target.value)}>
                {COUNTRIES.map(c => (
                  <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Zone di lavoro */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={[
                'Alessandria','Ancona','Aosta','Arezzo','Asti',
                'Bari','Benevento','Bergamo','Bolzano','Bologna','Brescia','Brindisi',
                'Cagliari','Campobasso','Caserta','Catania','Catanzaro','Como','Cosenza','Cuneo',
                'Estero',
                'Ferrara','Firenze','Foggia','Frosinone','Genova',
                "L'Aquila",'La Spezia','Latina','Lecce','Livorno',
                'Messina','Milano','Modena',
                'Napoli','Novara','Nuoro',
                'Padova','Palermo','Parma','Pescara','Perugia','Pisa','Potenza',
                'Ravenna','Reggio Calabria','Reggio Emilia','Rieti','Rimini','Roma',
                'Salerno','Sassari','Savona','Siena',
                'Taranto','Teramo','Torino','Trento','Treviso','Trieste',
                'Udine',
                'Varese','Venezia','Verona','Vicenza','Viterbo',
              ]}
              value={cities}
              onChange={(_, newVal) => setCities(newVal)}
              renderTags={(value, getTagProps) => (
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5 }}>
                  {value.map((opt, idx) => (
                    <Chip {...getTagProps({ index: idx })} key={opt} label={opt} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Zone di lavoro principali" placeholder="Aggiungi città" fullWidth sx={pillInputSx} />
              )}
              filterSelectedOptions
              sx={{ width:'100%', ...pillInputSx }}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
              <FormControlLabel
                control={<Switch checked={availableTravel} onChange={e => setAvailableTravel(e.target.checked)} color="primary" />}
                label="Disponibile a trasferte"
              />
              <FormControlLabel
                control={<Switch checked={availableRemote} onChange={e => setAvailableRemote(e.target.checked)} color="primary" />}
                label="Disponibile a smart working"
              />
            </Stack>
          </Grid>

          {/* Bio */}
<Grid item xs={12}>
  <Typography variant="overline" sx={{ color:'text.secondary', letterSpacing:'0.08em' }}>
    BIO
  </Typography>
  <TextField
    placeholder="Scrivi una breve bio (3–5 righe): specializzazioni, format TV, software principali…"
    multiline
    minRows={3}
    maxRows={8}
    value={bio}
    onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))}
    fullWidth
    inputProps={{ maxLength: BIO_MAX }}
    helperText={`${bio.length}/${BIO_MAX} caratteri`}
    sx={{ mt: .5 }}
  />
  {bio && bio.length < 80 && (
    <Typography variant="caption" sx={{ color:'warning.main' }}>
      Suggerimento: una bio di ~80+ caratteri funziona meglio.
    </Typography>
  )}
</Grid>


          {/* Skill */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              freeSolo
              options={skillsMaster}
              value={skills}
              onChange={(_, newVal) => {
                const cleaned = (newVal || [])
                  .map(s => (s || '').trim())
                  .filter(Boolean)
                  .filter((v, i, arr) => arr.findIndex(x => x.toLowerCase() === v.toLowerCase()) === i);
                setSkills(cleaned);
              }}
              renderTags={(value, getTagProps) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
    {value.map((opt, idx) => (
      <Chip
        {...getTagProps({ index: idx })}
        label={opt}
        size="small"
        color="warning"
        variant="outlined"
        sx={{
          fontWeight: 600,
          borderColor: 'warning.light',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    ))}
  </Box>
)}

              renderInput={(params) => (
  <TextField
    {...params}
    label="Skill"
    placeholder="Digita e premi Invio per aggiungere"
    fullWidth
    sx={{
      ...pillInputSx,
      '& .MuiInputBase-root': {
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        rowGap: 0.5,
      },
    }}
  />
)}
componentsProps={{ paper: { sx: { width: 520 } } }}  // ← sostituisce PaperProps

            />
            {/* Suggerimenti skill */}
{Array.isArray(skills) && (
  <Stack
  direction="row"
  spacing={1}
  rowGap={1}         // spazio verticale tra righe
  flexWrap="wrap"
  sx={{ mt: 1 }}
>
    {SKILL_PRESETS
      .filter(p => !skills.some(s => String(s).toLowerCase() === String(p).toLowerCase()))
      .map((p) => (
        <Chip
          size="small"
          key={p}
          label={p}
          variant="outlined"
          onClick={() => setSkills(prev => addUnique(prev ?? [], p))}
          sx={{ mb: 1, cursor: 'pointer' }}
        />
      ))}
  </Stack>
)}

          </Grid>

          {/* Azioni */}
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {/* Notifiche email */}
            <Box sx={{ mt: 3, mb: 2, p: 2, border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, background: 'rgba(212,165,80,0.03)', maxWidth: 400 }}>
              <Typography variant="body2" sx={{ fontFamily: 'Space Mono, monospace', fontSize: '10px', letterSpacing: '0.15em', color: '#8a8a9a', textTransform: 'uppercase', mb: 2 }}>
                Notifiche Email
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#f0ece4' }}>Nuovi alert compatibili con il mio ruolo</Typography>
                  <Switch checked={notifyAlerts} onChange={e => { setNotifyAlerts(e.target.checked); markDirty(); }} size="small" sx={{ '& .MuiSwitch-thumb': { bgcolor: notifyAlerts ? '#d4a550' : '#4a4a5a' } }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#f0ece4' }}>Nuovi messaggi ricevuti</Typography>
                  <Switch checked={notifyMessages} onChange={e => { setNotifyMessages(e.target.checked); markDirty(); }} size="small" sx={{ '& .MuiSwitch-thumb': { bgcolor: notifyMessages ? '#d4a550' : '#4a4a5a' } }} />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}>
              <button type="submit" disabled={saving}
                onMouseEnter={e => { if (!saving) e.target.style.background='#f0c878'; }}
                onMouseLeave={e => { e.target.style.background='#d4a550'; }}
                style={{ padding: '10px 24px', background: '#d4a550', color: '#07070a', border: 'none', borderRadius: '6px', fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, transition: 'background 0.2s' }}>
                {saving ? 'Salvataggio…' : 'Salva'}
              </button>
              {onCancel && (
                <button type="button"
                  onMouseEnter={e => { e.target.style.borderColor='rgba(255,255,255,0.3)'; e.target.style.color='#f0ece4'; }}
                  onMouseLeave={e => { e.target.style.borderColor='rgba(255,255,255,0.1)'; e.target.style.color='#8a8a9a'; }}
                  style={{ padding: '10px 20px', background: 'transparent', color: '#8a8a9a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', fontFamily: 'Kanit, sans-serif', fontWeight: 500, fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'border-color 0.2s, color 0.2s' }}
                  onClick={async () => {
                    const ok = await confirmIfDirty();
                    if (!ok) return;
                    onCancel();
                  }}>
                  Annulla
                </button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ---- Dialog Crop ---- */}
      <Dialog
        open={cropOpen}
        onClose={() => { setCropOpen(false); setImageSrc(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ritaglia immagine profilo</DialogTitle>
        <DialogContent sx={{ position: 'relative', height: 520, p: 0 }}>
          {imageSrc ? (
            <Box sx={{ position: 'absolute', inset: 0 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="cover"
              />
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>Seleziona un’immagine per procedere al ritaglio.</Box>
          )}
        </DialogContent>
        <Box sx={{ px: 3, pt: 1 }}>
          <Typography variant="caption">Zoom</Typography>
          <Slider
            size="small"
            min={1} max={3} step={0.05}
            value={zoom}
            onChange={(_, v) => setZoom(v)}
          />
        </Box>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => { setCropOpen(false); setImageSrc(null); }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              // Aggiorna SOLO la preview locale; l’upload avviene al SALVA
              if (imageSrc && croppedAreaPixels) {
                const file = await getCroppedFile(imageSrc, croppedAreaPixels);
                const reader = new FileReader();
                reader.onload = () => setProfileImageUrl(reader.result);
                reader.readAsDataURL(file);
              }
              setCropOpen(false);
            }}
          >
            Applica
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
